import { AIM_PRESETS, type AimPreset } from '../combat/autoaim';
import { WEAPON_ORDER, type WeaponId } from '../combat/weapon';
import { CAMERA_ZOOM_OPTIONS, type CameraZoom } from '../core/camera';
import { HUNT_TARGET_KINDS } from './hunts';
import type { EnemyKind } from '../ai/archetypes';
import { WEARABLES, isWearableId, type WearableId, type WearableSlot } from '../economy/wardrobe';

export const SAVE_VERSION = 'br3d.v1';
const SAVE_KEY = 'br3d.save';

export interface Settings {
  musicVolume: number; // 0..1
  sfxVolume: number; // 0..1
  shakeEnabled: boolean;
  flashEnabled: boolean;
  lefty: boolean;
  controlSize: number; // 0.8..1.3, applied via the --br3d-ctl-scale CSS var
  aimPreset: AimPreset;
  weaponId: WeaponId;
  /** T3-4 (PLAN-CITY2 §2) — persisted the same way `aimPreset`/`weaponId` already are (S07's own precedent). */
  cameraZoom: CameraZoom;
}

/**
 * A snapshot of "where the player actually is" — position, active
 * checkpoint, hp, and wallet. This game has no discrete levels yet (it's a
 * single persistent block, unlike Classic's mid-level suspend), so "suspend/
 * resume" here means genuine continuity across a reload rather than
 * Classic's mid-boss-fight snapshot. Never captured while the player is
 * dead/respawning (see main.ts's `snapshotSuspendedRun`) — resuming into a
 * dead state with no death UI showing would be a worse experience than just
 * keeping the last living snapshot.
 */
export interface SuspendedRun {
  playerX: number;
  playerZ: number;
  checkpointId: string;
  hp: number;
  coins: number;
  /** Mirrors economy/wallet.ts's current MaterialKind 'scrap'. A future material kind needs its own field here, same as Settings needs a new field for any new setting — this file has no generic Record salvage on purpose (an arbitrary-keyed object is much easier to get wrong when validating untrusted JSON). */
  scrap: number;
  /**
   * Wave L-1/L-3 (PLAN-CITY2 §6): the second MaterialKind, `part` —
   * `economy/wallet.ts`'s own header comment on `scrap` above literally
   * anticipated this. Review fix (HIGH, correctness): the field didn't exist
   * at all until this fix, so `wallet.materials.part` was silently discarded
   * on every reload — `economy/craft.ts`'s Riot Plating/Salvage Exchange
   * recipes were effectively unreachable outside one uninterrupted session.
   * Deliberately OPTIONAL-with-a-sensible-default in `salvageSave()` below
   * (like `insideLaundromat`/`insideLair`), NOT required alongside `scrap`/
   * `coins` — every suspended snapshot written before this fix landed has no
   * `part` key at all, and requiring it would drop the WHOLE suspended
   * object (position/hp/every other wallet field) for those saves the
   * instant this field shipped.
   */
  part: number;
  /**
   * Review finding (HIGH, confirmed independently by 2 lenses): without this
   * field, resuming into the bodega interior restored playerX/playerZ but
   * left `insideBodega` at its default `false`, so the camera used the
   * OUTDOOR pull-back distance inside the tiny interior room — reproducing
   * the exact S05 camera-occlusion bug (world/interior.ts's own header
   * comment) that a real door-entry already fixed. Any future scene flag
   * this game grows beyond "outdoors vs. bodega interior" needs the same
   * treatment: a `SuspendedRun` field + a matching capture/restore site.
   */
  insideBodega: boolean;
  /**
   * B-2 "Enterable interiors" (PLAN-CITY2 §3) — exactly the extension
   * `insideBodega`'s own comment above invited ("any future scene flag this
   * game grows beyond 'outdoors vs. bodega interior' needs the same
   * treatment"). At most one of `insideBodega`/`insideLaundromat`/
   * `insideBasement` is ever true during real play — an EMERGENT property
   * of each room's isolated origin being physically unreachable from
   * another's without passing back through real-world coordinates, plus
   * main.ts's own `tick()`-end self-healing invariants (position-distance
   * gated, so at most one can ever read "the player is near this origin").
   * Review finding (MEDIUM, confirmed independently by 2 lenses): NONE of
   * main.ts's `enterX`/`exitX` functions actually clear a sibling flag (an
   * earlier version of this comment claimed otherwise) — that guarantee
   * only ever comes from the mechanism above, which does not run until the
   * game loop's first `tick()`, AFTER boot has already called every
   * `block.setXVisible()` and the first camera snap. `salvageSave()` below
   * therefore DOES need its own cross-field guard (a hand-edited/corrupted
   * save could otherwise set more than one of these `true` at once,
   * rendering multiple interior groups simultaneously for at least one
   * frame before self-heal corrects it) — see that function's own comment
   * for the exact tie-break order.
   */
  insideLaundromat: boolean;
  insideBasement: boolean;
  /**
   * Wave D (PLAN-CITY2 §4) — the two den interiors, same extension
   * `insideLaundromat`/`insideBasement`'s own comment above already
   * invited. `salvageSave()`'s cross-field guard below is extended to
   * include these two in its own tie-break order.
   */
  insideLair: boolean;
  insideSewer: boolean;
}

/**
 * S10: durable, small progress flags — deliberately its own top-level field,
 * not folded into `SuspendedRun` (a periodic/transient position snapshot,
 * per that interface's own header comment) or `Settings` (user preferences).
 * Written immediately the instant a flag is earned (`main.ts`'s
 * `handleInteract`), the same "don't wait for the periodic snapshot"
 * discipline `applySettingsPatch` already uses — a found-but-uncommitted
 * flag lost to a crash/tab-close would let the hidden stash's one-time
 * reward be farmed again on the very next boot.
 */
/**
 * S11: an in-progress bodega hunt-board objective (PLAN-CITY §5's S11 line).
 * Reward terms are baked in at the moment the hunt is ACCEPTED (`meta/
 * hunts.ts`'s `HuntOption`, copied verbatim into this shape) rather than
 * re-derived later from `HUNT_TEMPLATES` by kind — the same fire-time-
 * capture discipline §0.7 already requires for a projectile's damage, now
 * applied to a reward: if a future session retunes the board's templates,
 * an already-in-progress hunt keeps paying what it promised when accepted.
 */
export interface ActiveHunt {
  kind: EnemyKind;
  targetCount: number;
  killedCount: number;
  rewardCoins: number;
  rewardScrap: number;
}

export interface Progress {
  hiddenStashFound: boolean;
  /** B-2 "Enterable interiors" (PLAN-CITY2 §3) — the laundromat's own one-time discovery reward, same shape as `hiddenStashFound`. */
  laundromatStashFound: boolean;
  /** B-1 "Rooftop network" (PLAN-CITY2 §3) — the rooftop vantage's own one-time discovery reward, same shape as `hiddenStashFound`. */
  rooftopVantageFound: boolean;
  /** S11: the ONE real craft recipe (PLAN-CITY §5's S11 line) — a one-time, permanent +max-HP unlock. Not stackable; once true, the crafting bench's `craft` interaction reads it and refuses a second craft. */
  armorCrafted: boolean;
  /** Wave L-3 (PLAN-CITY2 §6): the bench's second tier, `economy/craft.ts`'s Riot Plating — same one-time-permanent shape as `armorCrafted`, gated behind it (see that file's own `attemptNextCraftTier`). */
  platingCrafted: boolean;
  /** S11: flips true the instant the first-run guided flow fires (main.ts, boot-time) — gates that flow to a genuinely first-ever boot, never re-shown on a later session. */
  hasPlayedBefore: boolean;
  /** S11: lifetime count of hunt-board objectives completed — not consumed by anything yet, kept for a future difficulty/reward curve rather than invented here without a real use. */
  huntsCompleted: number;
  activeHunt: ActiveHunt | null;
  /**
   * Wave L-1 (PLAN-CITY2 §6): has THIS `EnemyKind` (or `BossLootKey`, e.g.
   * `'gridlock'`) ever been killed before — drives `economy/drops.ts`'s
   * guaranteed first-kill cosmetic grant (the Rat King's crown, the Golden
   * Pigeon's plume). A plain string-keyed map, not a `Record<EnemyKind,...>`
   * — `combat/kill.ts`'s `KillLootContext.everKilledFirstTime` is fed by a
   * `Set` built from this object's OWN keys (`Object.keys`), deliberately
   * decoupled from any specific kind enum the same way `KillLootContext`
   * itself is decoupled from `SaveData` (see that interface's own comment).
   */
  everKilled: Partial<Record<string, true>>;
  /**
   * Wave D-4 (PLAN-CITY2 §4): has this den EVER been cleared — separate from
   * `everKilled` (which drives the guaranteed first-KILL cosmetic roll,
   * Wave L-1's own territory) and from each den's own runtime `DenArena`/
   * `BossArena` state (world/arena.ts — cycles dormant/active/cleared every
   * single fight, never persisted). This flag is what gates the ONE-TIME
   * first-clear coin/scrap bonus (main.ts) — never resets once true.
   */
  ratKingLairCleared: boolean;
  sewerDenCleared: boolean;
  gridlockLairCleared: boolean;
  /**
   * Wave I (PLAN-CITY3 §5, I-4): which of the reusable "persistent
   * collectible" pickups (loot/pickup.ts's `spawnPersistentCollectible`)
   * have ever been found — arbitrary string-keyed, same "no fixed
   * vocabulary yet" shape as `everKilled` above, since Wave I ships only the
   * mechanism; E-3/V-3/N-1/N-2/N-4 invent the actual ids when they place
   * one. Once true, an id is permanently claimed (`spawnPersistentCollectible`
   * itself refuses to respawn an already-found one), never re-derived.
   */
  placedCollectibles: Partial<Record<string, true>>;
  /**
   * Review fix (HIGH, spec-fidelity): I-3's own line explicitly names this
   * as a required deliverable ("`Progress.stats` lifetime counters (coins/
   * scrap/parts/hearts/drops/killsByKind), salvaged key-by-key like
   * `everKilled`") — the first pass built only `ui/stash.ts`'s ephemeral
   * `SessionLog` (a THIS-SESSION delta that resets every boot, satisfying
   * the SEPARATE "[THIS RUN]" bullet in the same spec item) and missed this
   * durable half entirely. `coins`/`scrap`/`parts`/`hearts`/`drops` are
   * flat running totals (main.ts increments them at the same loot-
   * collection choke point `Progress.everKilled`'s own comment already
   * describes reading from); `killsByKind` mirrors `everKilled`'s own
   * arbitrary-string-keyed shape (an `EnemyKind` or a `BossLootKey` like
   * `'gridlock'`/`'rat_king'`), incremented once per confirmed kill.
   */
  stats: {
    coins: number;
    scrap: number;
    parts: number;
    hearts: number;
    drops: number;
    killsByKind: Partial<Record<string, number>>;
  };
}

/**
 * Wave L-2 (PLAN-CITY2 §6): which C09 cosmetics the player owns, and which
 * one (if any) is equipped in each of the 3 slots. `owned` only ever grows
 * (Classic gotcha-style "cosmetics are earned, never lost" convention — no
 * code path in this game removes an owned wearable); `equipped` can be
 * freely changed in the bodega wardrobe UI. S11's own migration lesson
 * ("a new save field's default must be correct for EXISTING players, not
 * just fresh ones") applies trivially here: every pre-L2 save has no
 * wardrobe at all, and "owns nothing, has nothing equipped" is the correct
 * default for that case — no special migration needed beyond the normal
 * per-field salvage below.
 */
export interface Wardrobe {
  owned: Partial<Record<WearableId, true>>;
  equipped: Record<WearableSlot, WearableId | null>;
}

export interface SaveData {
  version: typeof SAVE_VERSION;
  settings: Settings;
  suspended: SuspendedRun | null;
  progress: Progress;
  wardrobe: Wardrobe;
}

export const DEFAULT_SETTINGS: Settings = {
  musicVolume: 0.55,
  sfxVolume: 0.8,
  shakeEnabled: true,
  flashEnabled: true,
  lefty: false,
  controlSize: 1,
  aimPreset: 'lockon', // T1's shipped default (Karan's first-playtest note) — unchanged now that it lives here instead of its own ad-hoc localStorage key
  weaponId: 'nine',
  cameraZoom: 'normal', // T3-4 — identical to every pre-T3 build's fixed distance, so an upgrading player sees zero change until they touch the new setting
};

function reducedMotionPreferred(): boolean {
  // matchMedia is absent in some embedded/headless webviews (and in the
  // harness's own SwiftShader Chromium prior to a real user profile) —
  // never let a missing API crash the very first save a session ever loads.
  try {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function defaultSave(): SaveData {
  const settings = { ...DEFAULT_SETTINGS };
  // A genuinely first-ever boot (no prior save exists to override this) —
  // respect the OS-level accessibility signal for the two motion-sensitive
  // toggles. Anyone who explicitly re-enables them afterward has their
  // choice salvaged and kept exactly like any other setting; this only
  // shapes what a NEW player starts with.
  if (reducedMotionPreferred()) {
    settings.shakeEnabled = false;
    settings.flashEnabled = false;
  }
  return {
    version: SAVE_VERSION,
    settings,
    suspended: null,
    progress: {
      hiddenStashFound: false,
      laundromatStashFound: false,
      rooftopVantageFound: false,
      armorCrafted: false,
      platingCrafted: false,
      hasPlayedBefore: false,
      huntsCompleted: 0,
      activeHunt: null,
      everKilled: {},
      ratKingLairCleared: false,
      sewerDenCleared: false,
      gridlockLairCleared: false,
      placedCollectibles: {},
      stats: { coins: 0, scrap: 0, parts: 0, hearts: 0, drops: 0, killsByKind: {} },
    },
    wardrobe: { owned: {}, equipped: { head: null, torso: null, back: null } },
  };
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * Per-field salvage (Classic gotcha #26: a raw parse never assigns to the
 * live save). `raw` is untrusted `unknown` — a hand-edited localStorage
 * value, a future save version's extra fields, or simply `null` on a first
 * boot. Every field is individually type/range-checked against a fresh
 * default object; anything that fails is silently left at its default
 * rather than thrown or propagated (a malformed `controlSize` must never
 * reach `transform: scale()`, a bogus `aimPreset` string must never reach
 * `resolveAimDirection`). Exported (not just used internally by `loadSave`)
 * so the harness can exercise it directly against synthetic bad input,
 * matching this project's "expose the raw function for direct testing"
 * convention (e.g. `resolveAimDirectionRaw` on the debug hook).
 */
export function salvageSave(raw: unknown): SaveData {
  const out = defaultSave();
  if (typeof raw !== 'object' || raw === null) return out;
  const r = raw as Record<string, unknown>;

  // S11 review fix (HIGH, correctness + don't-break-list, independently
  // caught by two review lenses): `hasPlayedBefore` is a brand-new field —
  // every save written before this session (S07-S10's real schema, which
  // this game's own dev save and every future upgrading player already has)
  // has no such key at all, so the per-field check below would otherwise
  // leave it at defaultSave()'s `false` and main.ts's `isFirstRun` would
  // misidentify a genuine returning player as a brand-new one: shown the
  // "Welcome to Astoria" toast, teleported into the bodega instead of their
  // real resumed position (briefly, until the `suspended` block below
  // corrects it later in main.ts's own boot order), and — not corrected by
  // anything — `save.progress.activeHunt` silently force-overwritten with
  // STARTER_HUNT and persisted before the player ever touched the hunt
  // board. The fix: by the time `salvageSave` is reading a real parsed
  // OBJECT (not the `raw===null`/non-object case already returned above,
  // which correctly stays `false` — that IS a genuinely first-ever boot),
  // SOME save was found in localStorage at all, which itself proves the
  // player has played before, regardless of whether this specific field
  // exists yet — the same class of fix `migrateLegacySettings` below already
  // shipped once for `weaponId`/`aimPreset`, generalized here to a single
  // default flip instead of a second parallel-key migration function, since
  // "a save object existed" is a strictly more universal signal than any
  // one specific legacy field. An explicit `hasPlayedBefore` value in the
  // raw JSON (true OR false) still overrides this default via the normal
  // per-field check further down — this only changes what an ABSENT key
  // defaults to.
  out.progress.hasPlayedBefore = true;

  const s = r.settings;
  if (typeof s === 'object' && s !== null) {
    const rs = s as Record<string, unknown>;
    if (isFiniteNumber(rs.musicVolume)) out.settings.musicVolume = clamp01(rs.musicVolume);
    if (isFiniteNumber(rs.sfxVolume)) out.settings.sfxVolume = clamp01(rs.sfxVolume);
    if (typeof rs.shakeEnabled === 'boolean') out.settings.shakeEnabled = rs.shakeEnabled;
    if (typeof rs.flashEnabled === 'boolean') out.settings.flashEnabled = rs.flashEnabled;
    if (typeof rs.lefty === 'boolean') out.settings.lefty = rs.lefty;
    if (isFiniteNumber(rs.controlSize)) out.settings.controlSize = Math.min(1.3, Math.max(0.8, rs.controlSize));
    if (typeof rs.aimPreset === 'string' && (AIM_PRESETS as readonly string[]).includes(rs.aimPreset)) out.settings.aimPreset = rs.aimPreset as AimPreset;
    if (typeof rs.weaponId === 'string' && (WEAPON_ORDER as readonly string[]).includes(rs.weaponId)) out.settings.weaponId = rs.weaponId as WeaponId;
    // T3-4: absent on every pre-T3 save (the field didn't exist) — falls
    // through to defaultSave()'s 'normal', same "an absent field keeps the
    // default" behavior every other Settings field here already has, not a
    // special-cased migration (unlike weaponId/aimPreset's OWN legacy-key
    // migration below, which exists only because THOSE fields used to live
    // under different top-level localStorage keys entirely — cameraZoom
    // never had a prior key to migrate FROM).
    if (typeof rs.cameraZoom === 'string' && (CAMERA_ZOOM_OPTIONS as readonly string[]).includes(rs.cameraZoom)) out.settings.cameraZoom = rs.cameraZoom as CameraZoom;
  }

  const susp = r.suspended;
  if (typeof susp === 'object' && susp !== null) {
    const rsu = susp as Record<string, unknown>;
    if (
      isFiniteNumber(rsu.playerX) &&
      isFiniteNumber(rsu.playerZ) &&
      typeof rsu.checkpointId === 'string' &&
      isFiniteNumber(rsu.hp) &&
      isFiniteNumber(rsu.coins) &&
      isFiniteNumber(rsu.scrap) &&
      typeof rsu.insideBodega === 'boolean'
    ) {
      out.suspended = {
        playerX: rsu.playerX,
        playerZ: rsu.playerZ,
        checkpointId: rsu.checkpointId,
        hp: Math.max(0, rsu.hp),
        coins: Math.max(0, rsu.coins),
        scrap: Math.max(0, rsu.scrap),
        // Wave L-1/L-3 review fix: absent-defaults-to-0, NOT required in the
        // validation `if` above — see the interface field's own comment.
        part: isFiniteNumber(rsu.part) ? Math.max(0, rsu.part) : 0,
        insideBodega: rsu.insideBodega,
        // B-2: absent on every pre-B2 save (these fields didn't exist yet) —
        // default to false rather than REQUIRING them, which would drop the
        // WHOLE suspended snapshot (position/hp/wallet — see this interface's
        // own `insideBodega` field comment: there is no OTHER place wallet
        // state persists) for every existing player's save the instant this
        // field was added. Same "an absent field defaults sensibly, it
        // doesn't invalidate an otherwise-structurally-valid object" lesson
        // S11 already established for its own isFirstRun-style migrations.
        insideLaundromat: typeof rsu.insideLaundromat === 'boolean' ? rsu.insideLaundromat : false,
        insideBasement: typeof rsu.insideBasement === 'boolean' ? rsu.insideBasement : false,
        // Wave D: same absent-defaults-false treatment as insideLaundromat/insideBasement above — this field didn't exist on any pre-Wave-D save.
        insideLair: typeof rsu.insideLair === 'boolean' ? rsu.insideLair : false,
        insideSewer: typeof rsu.insideSewer === 'boolean' ? rsu.insideSewer : false,
      };
      // Review finding (MEDIUM, confirmed independently by 2 lenses): the
      // three fields above are each individually type-validated, but
      // nothing above stops a hand-edited or corrupted save from setting
      // MORE THAN ONE true at once — at most one is ever supposed to be
      // true (main.ts's own three rooms are mutually exclusive in real
      // play, but that's an EMERGENT property of physical unreachability
      // + tick()'s own self-healing invariant, not something any enter*
      // function enforces directly — the code comment that used to claim
      // otherwise was itself wrong, per this same review). Left unguarded,
      // a save with e.g. insideBodega AND insideLaundromat both true would
      // make main.ts's boot sequence set BOTH interior groups visible
      // simultaneously for at least the first rendered frame, before
      // tick()'s own self-heal (gated on real player position, which can
      // only ever be near ONE origin) corrects it on the next tick. Same
      // priority order as main.ts's own activeCameraMaxDistance() so the
      // "which one wins" choice is consistent across both call sites.
      // Wave D: extends the SAME guard to the two new rooms — priority order
      // (bodega > laundromat > basement > lair > sewer) is arbitrary among
      // ties (real play can never produce one), just deterministic.
      if (out.suspended.insideLaundromat || out.suspended.insideBasement || out.suspended.insideLair || out.suspended.insideSewer) {
        if (out.suspended.insideBodega) {
          out.suspended.insideLaundromat = false;
          out.suspended.insideBasement = false;
          out.suspended.insideLair = false;
          out.suspended.insideSewer = false;
        } else if (out.suspended.insideLaundromat) {
          out.suspended.insideBasement = false;
          out.suspended.insideLair = false;
          out.suspended.insideSewer = false;
        } else if (out.suspended.insideBasement) {
          out.suspended.insideLair = false;
          out.suspended.insideSewer = false;
        } else if (out.suspended.insideLair) {
          out.suspended.insideSewer = false;
        }
      }
    }
    // else: a `suspended` object that fails structural validation is
    // dropped WHOLE (stays null), never partially adopted — same "any doubt
    // -> null" law Classic's own `save.suspended` schema used (gotcha #43).
  }

  const prog = r.progress;
  if (typeof prog === 'object' && prog !== null) {
    const rp = prog as Record<string, unknown>;
    if (typeof rp.hiddenStashFound === 'boolean') out.progress.hiddenStashFound = rp.hiddenStashFound;
    if (typeof rp.laundromatStashFound === 'boolean') out.progress.laundromatStashFound = rp.laundromatStashFound;
    if (typeof rp.rooftopVantageFound === 'boolean') out.progress.rooftopVantageFound = rp.rooftopVantageFound;
    if (typeof rp.armorCrafted === 'boolean') out.progress.armorCrafted = rp.armorCrafted;
    // Wave L-3: platingCrafted is gated behind armorCrafted at the CRAFT-TIME
    // check (economy/craft.ts), not here — a hand-edited save with
    // platingCrafted:true but armorCrafted:false is a plausible-if-odd state
    // (skipped straight to tier 2) that doesn't actually break anything
    // downstream (the bench just re-offers Riot Plating, already granted,
    // next `armorCrafted` check simply also passes), so it's adopted as-is
    // rather than cross-validated against a sibling field.
    if (typeof rp.platingCrafted === 'boolean') out.progress.platingCrafted = rp.platingCrafted;
    if (typeof rp.hasPlayedBefore === 'boolean') out.progress.hasPlayedBefore = rp.hasPlayedBefore;
    if (isFiniteNumber(rp.huntsCompleted)) out.progress.huntsCompleted = Math.max(0, Math.floor(rp.huntsCompleted));
    // Wave D-4: one-time-forever den-cleared flags, same shape as hiddenStashFound above.
    if (typeof rp.ratKingLairCleared === 'boolean') out.progress.ratKingLairCleared = rp.ratKingLairCleared;
    if (typeof rp.sewerDenCleared === 'boolean') out.progress.sewerDenCleared = rp.sewerDenCleared;
    if (typeof rp.gridlockLairCleared === 'boolean') out.progress.gridlockLairCleared = rp.gridlockLairCleared;

    // Wave L-1: an arbitrary-keyed object, salvaged key-by-key (only string
    // keys with value `true` survive) rather than requiring a known kind
    // enum — `everKilled`'s own keys are plain strings by design (see
    // Progress.everKilled's own comment), so there's no fixed vocabulary to
    // validate against the way `activeHunt.kind` validates against
    // `HUNT_TARGET_KINDS`.
    const ek = rp.everKilled;
    if (typeof ek === 'object' && ek !== null) {
      for (const [k, v] of Object.entries(ek as Record<string, unknown>)) {
        if (v === true) out.progress.everKilled[k] = true;
      }
    }

    // Wave I: same key-by-key salvage as everKilled above — no fixed id
    // vocabulary exists yet (no persistent collectible has ever been placed
    // by any wave), so there's nothing to validate each key AGAINST beyond
    // "a string key with value true."
    const pc = rp.placedCollectibles;
    if (typeof pc === 'object' && pc !== null) {
      for (const [k, v] of Object.entries(pc as Record<string, unknown>)) {
        if (v === true) out.progress.placedCollectibles[k] = true;
      }
    }

    // Review fix (HIGH, spec-fidelity): "salvaged key-by-key like everKilled"
    // — the 5 flat counters are individually type/range-checked (a negative
    // or non-finite value is rejected, same discipline every other numeric
    // field in this function uses); killsByKind is salvaged key-by-key with
    // no fixed vocabulary to validate against, same as everKilled/
    // placedCollectibles above (only non-negative finite numeric values
    // survive per key).
    const st = rp.stats;
    if (typeof st === 'object' && st !== null) {
      const rst = st as Record<string, unknown>;
      if (isFiniteNumber(rst.coins)) out.progress.stats.coins = Math.max(0, rst.coins);
      if (isFiniteNumber(rst.scrap)) out.progress.stats.scrap = Math.max(0, rst.scrap);
      if (isFiniteNumber(rst.parts)) out.progress.stats.parts = Math.max(0, rst.parts);
      if (isFiniteNumber(rst.hearts)) out.progress.stats.hearts = Math.max(0, rst.hearts);
      if (isFiniteNumber(rst.drops)) out.progress.stats.drops = Math.max(0, rst.drops);
      const kbk = rst.killsByKind;
      if (typeof kbk === 'object' && kbk !== null) {
        for (const [k, v] of Object.entries(kbk as Record<string, unknown>)) {
          if (isFiniteNumber(v) && v >= 0) out.progress.stats.killsByKind[k] = v;
        }
      }
    }

    // Structural validation, same "any doubt -> drop the WHOLE object" law
    // as `suspended` above (Classic gotcha #43) — a partially-valid hunt
    // (e.g. a real kind but a negative killedCount) is just as untrustworthy
    // as a fully malformed one, so nothing here is adopted field-by-field.
    const ah = rp.activeHunt;
    if (typeof ah === 'object' && ah !== null) {
      const rah = ah as Record<string, unknown>;
      if (
        typeof rah.kind === 'string' &&
        (HUNT_TARGET_KINDS as readonly string[]).includes(rah.kind) &&
        isFiniteNumber(rah.targetCount) &&
        rah.targetCount > 0 &&
        isFiniteNumber(rah.killedCount) &&
        rah.killedCount >= 0 &&
        isFiniteNumber(rah.rewardCoins) &&
        rah.rewardCoins >= 0 &&
        isFiniteNumber(rah.rewardScrap) &&
        rah.rewardScrap >= 0
      ) {
        const targetCount = Math.floor(rah.targetCount);
        out.progress.activeHunt = {
          kind: rah.kind as EnemyKind,
          targetCount,
          // Clamped to targetCount — a killedCount that somehow exceeds its
          // own target (a hand-edited value, a future bug) must never read
          // as "still in progress but over 100%" to any consumer.
          killedCount: Math.min(Math.floor(rah.killedCount), targetCount),
          rewardCoins: Math.floor(rah.rewardCoins),
          rewardScrap: Math.floor(rah.rewardScrap),
        };
      }
      // else: dropped whole — `out.progress.activeHunt` stays at defaultSave()'s own `null`.
    }
    // `ah === null` (a genuinely saved "no active hunt" state) correctly
    // falls through here with no branch taken — `out.progress.activeHunt`
    // is already `null` from `defaultSave()`, so there's nothing to assign.
  }

  // Wave L-2: `owned` is validated key-by-key against the REAL `WearableId`
  // vocabulary (unlike `everKilled` above, which has no fixed vocabulary to
  // check against) — an unknown id in a hand-edited/future-version save is
  // silently dropped rather than adopted, so a stale wardrobe UI can never
  // be asked to render a piece it doesn't recognize. `equipped` is then
  // cross-validated against the JUST-SALVAGED `owned` set (not the raw
  // input) — a slot can never end up "equipped" with something the save
  // doesn't actually claim to own, and each slot's own value must additionally
  // match that WEARABLE's real slot (an `equipped.head` pointing at a `back`
  // piece is exactly as untrustworthy as an unowned one).
  const wd = r.wardrobe;
  if (typeof wd === 'object' && wd !== null) {
    const rw = wd as Record<string, unknown>;
    const owned = rw.owned;
    if (typeof owned === 'object' && owned !== null) {
      for (const [k, v] of Object.entries(owned as Record<string, unknown>)) {
        if (v === true && isWearableId(k)) out.wardrobe.owned[k] = true;
      }
    }
    const equipped = rw.equipped;
    if (typeof equipped === 'object' && equipped !== null) {
      const req = equipped as Record<string, unknown>;
      for (const slot of ['head', 'torso', 'back'] as const) {
        const v = req[slot];
        if (v === null) continue; // already null in defaultSave()'s own object — nothing to assign
        if (typeof v === 'string' && isWearableId(v) && out.wardrobe.owned[v] && WEARABLES[v].slot === slot) {
          out.wardrobe.equipped[slot] = v;
        }
      }
    }
  }

  return out;
}

// Review finding (MEDIUM): S04/T2 persisted the equipped weapon and aim
// preset under these two standalone keys before this file existed. A
// player who already had one of these set and then loads a build with no
// migration would have `loadSave()` fall through to `defaultSave()` and
// silently lose their choice the first time `br3d.save` doesn't exist yet
// — read once, on exactly that boot, and folded into the fresh save.
const LEGACY_WEAPON_KEY = 'br3d.weapon';
const LEGACY_AIM_KEY = 'br3d.aim';

function migrateLegacySettings(settings: Settings): void {
  try {
    const w = localStorage.getItem(LEGACY_WEAPON_KEY);
    if (w !== null && (WEAPON_ORDER as readonly string[]).includes(w)) settings.weaponId = w as WeaponId;
    const a = localStorage.getItem(LEGACY_AIM_KEY);
    if (a !== null && (AIM_PRESETS as readonly string[]).includes(a)) settings.aimPreset = a as AimPreset;
  } catch {
    // storage unavailable — nothing to migrate
  }
}

/** Boot-time load — the ONLY place `localStorage` is read for the save. Never throws (a corrupt JSON blob or an unavailable store, e.g. private browsing, falls back to a fresh default) — a save failure must never block the game from starting. */
export function loadSave(): SaveData {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (raw === null) {
      const save = defaultSave();
      migrateLegacySettings(save.settings); // only reachable on the ONE boot before br3d.save exists at all — never touches an already-migrated or already-fresh save
      return save;
    }
    return salvageSave(JSON.parse(raw));
  } catch {
    return defaultSave();
  }
}

/** Best-effort write — a full/unavailable quota must not crash whatever gameplay moment triggered the save. */
export function writeSave(save: SaveData): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(save));
  } catch {
    // best effort
  }
}
